import { useState, useRef } from 'react';
import {
    Button, Table, Tag, Alert, Divider, Space, Typography, Tooltip, message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
    FileSearchOutlined, WarningOutlined, PlayCircleOutlined, StopOutlined,
    LoadingOutlined, CheckCircleOutlined, CloseCircleOutlined, ExclamationCircleOutlined,
    DownloadOutlined, MinusCircleOutlined,
} from '@ant-design/icons';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import styled from 'styled-components';
import { slideFileCheck } from '../../api/adminAPI';
import { getBoardListWithRootCategory, newGetCategory } from '../../api/categoryAPI';
import { getPostList, getPostData } from '../../api/postAPI';
import { checkSlideQC } from '../../api/deepzoomAPI';
import { saveSlideQCResult } from '../../api/slideAPI';

const { Text } = Typography;

type QCStatus = 'loading' | 'valid' | 'invalid' | 'error' | 'skipped';

const HEAVY_EXTENSIONS = ['mrxs', 'ndpi', 'zip'];

interface QCRow {
    key: string;
    postId: number;
    postTitle: string;
    categoryPath: string;
    slideId: number;
    slideName: string;
    filename: string;
    status: QCStatus;
    checkedTiles?: number;
    corruptedCount?: number;
    elapsedMs?: number;
    errorMsg?: string;
}


const statusTag = (row: QCRow) => {
    switch (row.status) {
        case 'loading':
            return <Tag icon={<LoadingOutlined />} color="processing">검사 중</Tag>;
        case 'valid':
            return <Tag icon={<CheckCircleOutlined />} color="success">정상</Tag>;
        case 'invalid':
            return <Tag icon={<CloseCircleOutlined />} color="error">손상</Tag>;
        case 'error':
            return (
                <Tooltip title={row.errorMsg}>
                    <Tag icon={<ExclamationCircleOutlined />} color="warning">오류</Tag>
                </Tooltip>
            );
        case 'skipped':
            return <Tag icon={<MinusCircleOutlined />} color="default">미실시</Tag>;
    }
};

const columns: ColumnsType<QCRow> = [
    {
        title: '카테고리',
        dataIndex: 'categoryPath',
        ellipsis: true,
        width: 220,
    },
    {
        title: '포스트',
        dataIndex: 'postTitle',
        ellipsis: true,
        width: 180,
    },
    {
        title: 'Slide ID',
        dataIndex: 'slideId',
        width: 80,
        align: 'right' as const,
    },
    {
        title: '슬라이드명',
        dataIndex: 'slideName',
        ellipsis: true,
        width: 150,
    },
    {
        title: '파일명',
        dataIndex: 'filename',
        ellipsis: true,
        width: 160,
    },
    {
        title: '상태',
        width: 100,
        render: (_, row) => statusTag(row),
    },
    {
        title: '검사 타일',
        dataIndex: 'checkedTiles',
        width: 90,
        align: 'right',
        render: (v) => v ?? '-',
    },
    {
        title: '손상 타일',
        dataIndex: 'corruptedCount',
        width: 90,
        align: 'right',
        render: (v: number | undefined, row) => {
            if (v === undefined) return '-';
            if (v > 0) return <Text type="danger" strong>{v}</Text>;
            return <Text type="success">{v}</Text>;
        },
    },
    {
        title: '소요시간',
        dataIndex: 'elapsedMs',
        width: 90,
        align: 'right',
        render: (v) => v !== undefined ? `${v}ms` : '-',
    },
];

const SlideFileCheckPage = () => {
    const [legacyLoading, setLegacyLoading] = useState(false);

    const [rows, setRows] = useState<QCRow[]>([]);
    const [scanning, setScanning] = useState(false);
    const [scanDone, setScanDone] = useState(false);
    const [progressText, setProgressText] = useState('');
    const stopRef = useRef(false);

    const handleLegacyCheck = async () => {
        setLegacyLoading(true);
        try {
            await slideFileCheck();
            message.success('파일 체크 요청이 완료되었습니다.');
        } catch {
            message.error('파일 체크 요청에 실패했습니다.');
        } finally {
            setLegacyLoading(false);
        }
    };

    const handleStartQC = async () => {
        stopRef.current = false;
        setRows([]);
        setScanDone(false);
        setScanning(true);

        const collectCategories = async (parentId: number, pathPrefix: string, pathMap: Map<number, string>) => {
            if (stopRef.current) return;
            const children = await newGetCategory(parentId);
            if (!children?.length) return;
            for (const cat of children) {
                const fullPath = `${pathPrefix} > ${cat.name}`;
                pathMap.set(cat.id, fullPath);
                await collectCategories(cat.id, fullPath, pathMap);
            }
        };

        try {
            setProgressText('카테고리 목록 수집 중...');
            const boards = await getBoardListWithRootCategory() || [];
            const pathMap = new Map<number, string>();
            for (const board of boards) {
                const rootId = board.rootCategories?.[0]?.id;
                if (!rootId) continue;
                pathMap.set(rootId, board.name);
                await collectCategories(rootId, board.name, pathMap);
            }
            const categoryIds = [...pathMap.keys()];
            const seenPostIds = new Set<number>();
            const posts: { postId: number; postTitle: string; categoryPath: string }[] = [];

            for (let ci = 0; ci < categoryIds.length; ci++) {
                if (stopRef.current) break;
                const catId = categoryIds[ci];
                setProgressText(`카테고리 포스트 수집 중 (${ci + 1}/${categoryIds.length})`);

                const catPosts = await getPostList(1, 999, catId, 'createdAt', 'DESC') || [];
                for (const post of catPosts) {
                    if (!seenPostIds.has(post.id)) {
                        seenPostIds.add(post.id);
                        posts.push({
                            postId: post.id,
                            postTitle: post.title || `#${post.id}`,
                            categoryPath: pathMap.get(catId) ?? `Category ${catId}`,
                        });
                    }
                }
            }

            if (posts.length === 0) {
                setProgressText('검사할 포스트가 없습니다.');
                setScanDone(true);
                return;
            }

            for (let pi = 0; pi < posts.length; pi++) {
                if (stopRef.current) break;

                const { postId, postTitle, categoryPath } = posts[pi];
                setProgressText(`포스트 슬라이드 검사 중 (${pi + 1}/${posts.length}): ${postTitle}`);

                let postData: any;
                try {
                    postData = await getPostData(postId);
                } catch {
                    continue;
                }

                const slides: any[] = postData?.slides || [];
                if (slides.length === 0) continue;

                const qcResultToStatus: Record<string, QCStatus> = {
                    PASS: 'valid', FAIL: 'invalid', ERROR: 'error', SKIPPED: 'skipped',
                };

                for (const slide of slides) {
                    if (stopRef.current) break;

                    const rowKey = `${postId}-${slide.slideId}`;
                    const ext = slide.filename?.split('.').pop()?.toLowerCase() ?? '';
                    const isHeavy = HEAVY_EXTENSIONS.includes(ext);

                    // 이미 검사된 슬라이드는 기존 결과로 바로 표시, 재검사 스킵
                    if (slide.qcResult && qcResultToStatus[slide.qcResult]) {
                        setRows(prev => [...prev, {
                            key: rowKey,
                            postId,
                            postTitle,
                            categoryPath,
                            slideId: slide.slideId,
                            slideName: slide.slidename || slide.filename,
                            filename: slide.filename,
                            status: qcResultToStatus[slide.qcResult],
                            errorMsg: slide.qcResult === 'SKIPPED' ? '대용량 파일 — QC 미실시' : undefined,
                        }]);
                        continue;
                    }

                    setRows(prev => [...prev, {
                        key: rowKey,
                        postId,
                        postTitle,
                        categoryPath,
                        slideId: slide.slideId,
                        slideName: slide.slidename || slide.filename,
                        filename: slide.filename,
                        status: isHeavy ? 'skipped' : 'loading',
                        errorMsg: isHeavy ? '대용량 파일 — QC 미실시' : undefined,
                    }]);

                    if (isHeavy) {
                        saveSlideQCResult(slide.slideId, 'SKIPPED');
                        continue;
                    }

                    try {
                        const result = await checkSlideQC(postId, slide.filename);
                        const corruptedCount = result.corrupted_tiles.length;
                        const isValid = result.valid || corruptedCount === 0;
                        setRows(prev => prev.map(r =>
                            r.key === rowKey ? {
                                ...r,
                                status: isValid ? 'valid' : 'invalid',
                                checkedTiles: result.checked,
                                corruptedCount,
                                elapsedMs: result.elapsed_ms,
                                errorMsg: result.error,
                            } : r
                        ));
                        saveSlideQCResult(slide.slideId, isValid ? 'PASS' : 'FAIL');
                    } catch (e: any) {
                        const errorMsg = e?.response?.status === 404
                            ? '파일 없음 (딥줌 서버에 미존재)'
                            : `검사 실패 (${e?.response?.status ?? 'network error'})`;
                        setRows(prev => prev.map(r =>
                            r.key === rowKey ? { ...r, status: 'error', errorMsg } : r
                        ));
                        saveSlideQCResult(slide.slideId, 'ERROR');
                    }
                }
            }

            if (!stopRef.current) setScanDone(true);
        } catch (e) {
            console.error('QC 스캔 중 오류:', e);
            message.error('QC 스캔 중 오류가 발생했습니다.');
        } finally {
            setScanning(false);
            setProgressText('');
        }
    };

    const handleStop = () => {
        stopRef.current = true;
    };

    const handleDownloadExcel = () => {
        const statusLabel: Record<QCStatus, string> = {
            loading: '검사 중',
            valid: '정상',
            invalid: '손상',
            error: '오류',
            skipped: '미실시',
        };

        const sheetData = rows.map((r, i) => ({
            'No.': i + 1,
            '카테고리': r.categoryPath,
            '포스트': r.postTitle,
            '포스트 ID': r.postId,
            '슬라이드명': r.slideName,
            '파일명': r.filename,
            '상태': statusLabel[r.status],
            '검사 타일': r.checkedTiles ?? '',
            '손상 타일': r.corruptedCount ?? '',
            '소요시간(ms)': r.elapsedMs ?? '',
            '오류 메시지': r.errorMsg ?? '',
        }));

        const ws = XLSX.utils.json_to_sheet(sheetData);
        ws['!cols'] = [
            { wch: 5 }, { wch: 40 }, { wch: 30 }, { wch: 10 },
            { wch: 25 }, { wch: 30 }, { wch: 8 }, { wch: 10 },
            { wch: 10 }, { wch: 12 }, { wch: 40 },
        ];

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'QC 결과');

        const now = new Date();
        const timestamp = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}`;
        const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        saveAs(new Blob([buf], { type: 'application/octet-stream' }), `SlideQC_${timestamp}.xlsx`);
    };

    const stats = {
        total: rows.length,
        valid: rows.filter(r => r.status === 'valid').length,
        invalid: rows.filter(r => r.status === 'invalid').length,
        error: rows.filter(r => r.status === 'error').length,
        skipped: rows.filter(r => r.status === 'skipped').length,
        loading: rows.filter(r => r.status === 'loading').length,
    };

    return (
        <Wrapper>
            <Section>
                <Title>슬라이드 파일 체크</Title>
                <Notice>
                    <WarningOutlined style={{ marginRight: 8 }} />
                    서버용 기능입니다. humintec 계정만 접근 가능합니다.
                </Notice>
                <DoNotPress>누르지 마세요</DoNotPress>
                <Button
                    type="primary"
                    icon={<FileSearchOutlined />}
                    loading={legacyLoading}
                    onClick={handleLegacyCheck}
                    size="large"
                    danger
                >
                    파일 체크 실행
                </Button>
            </Section>

            <Divider />

            <Section>
                <Title>슬라이드 QC 검사</Title>
                <p style={{ margin: '0 0 16px', color: '#595959' }}>
                    전체 카테고리를 순회하며 각 슬라이드 파일의 타일 무결성을 검사합니다.
                </p>

                <Space>
                    <Button
                        type="primary"
                        icon={<PlayCircleOutlined />}
                        onClick={handleStartQC}
                        disabled={scanning}
                        size="large"
                    >
                        QC 스캔 시작
                    </Button>
                    {scanning && (
                        <Button
                            icon={<StopOutlined />}
                            onClick={handleStop}
                            size="large"
                            danger
                        >
                            중단
                        </Button>
                    )}
                    {!scanning && rows.length > 0 && (
                        <Button
                            icon={<DownloadOutlined />}
                            onClick={handleDownloadExcel}
                            size="large"
                        >
                            엑셀 다운로드
                        </Button>
                    )}
                </Space>

                {(scanning || rows.length > 0) && (
                    <StatsBar>
                        <StatItem>전체 <StatNum>{stats.total}</StatNum></StatItem>
                        <StatItem><Text type="success">정상 {stats.valid}</Text></StatItem>
                        <StatItem><Text type="danger">손상 {stats.invalid}</Text></StatItem>
                        <StatItem><Text style={{ color: '#faad14' }}>오류 {stats.error}</Text></StatItem>
                        {stats.skipped > 0 && (
                            <StatItem><Text type="secondary">미실시 {stats.skipped}</Text></StatItem>
                        )}
                        {stats.loading > 0 && (
                            <StatItem><Text type="secondary">대기 {stats.loading}</Text></StatItem>
                        )}
                    </StatsBar>
                )}

                {scanning && progressText && (
                    <Alert type="info" message={progressText} showIcon style={{ marginBottom: 12 }} />
                )}

                {scanDone && stats.invalid === 0 && stats.error === 0 && stats.total > 0 && (
                    <Alert type="success" message="모든 슬라이드 파일이 정상입니다." showIcon style={{ marginBottom: 12 }} />
                )}
                {scanDone && (stats.invalid > 0 || stats.error > 0) && (
                    <Alert
                        type="error"
                        message={`손상 ${stats.invalid}개, 오류 ${stats.error}개가 발견되었습니다.`}
                        showIcon
                        style={{ marginBottom: 12 }}
                    />
                )}

                {rows.length > 0 && (
                    <Table
                        columns={columns}
                        dataSource={rows}
                        size="small"
                        pagination={{ pageSize: 50, showSizeChanger: true }}
                        scroll={{ x: 1200 }}
                        rowClassName={(row) => row.status === 'invalid' ? 'qc-row-invalid' : ''}
                    />
                )}
            </Section>
        </Wrapper>
    );
};

const Wrapper = styled.div`
    padding: 40px;
    display: flex;
    flex-direction: column;
    gap: 0;

    .qc-row-invalid td {
        background-color: #fff1f0 !important;
    }
`;

const Section = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
`;

const Title = styled.h2`
    margin: 0;
    font-size: 20px;
    font-weight: 600;
`;

const Notice = styled.p`
    margin: 0;
    color: #595959;
    font-size: 14px;
`;

const DoNotPress = styled.p`
    margin: 0;
    color: #ff4d4f;
    font-size: 16px;
    font-weight: 700;
`;

const StatsBar = styled.div`
    display: flex;
    gap: 24px;
    align-items: center;
    padding: 12px 16px;
    background: #fafafa;
    border: 1px solid #f0f0f0;
    border-radius: 6px;
    flex-wrap: wrap;
`;

const StatItem = styled.span`
    font-size: 14px;
`;

const StatNum = styled.strong`
    margin-left: 4px;
`;

export default SlideFileCheckPage;
